import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import MyCropper from "../../tools/MyCropper";
import MyCropperComp from "../../tools/MyCrooperComp";
import Detailes from "../card/cardDeailes";
import { setPreload } from "../../../server/CartsOpen";
import { getPresignedUrl } from "../../../server/Filter";
import {
  getItemsPerCategory,
  updateProductFields,
  UpdateProduct,
} from "../../../server/Product";
import showAlert, {
  showAlertWithConfirmCancel,
} from "../../../server/ShowAlert";
import { newGlobalFileServer, globalFileIcon } from "../../../../axiosInstance";

const DEMO_PRODUCTS_COUNT = 6;

const buildImageUrlCandidates = (img) => {
  if (!img?.path) return [];

  if (img.path.startsWith("data:") || img.path.startsWith("blob:")) {
    return [img.path];
  }

  const cleanPath = img.path.startsWith("/") ? img.path.substring(1) : img.path;

  return [`${newGlobalFileServer}${cleanPath}`];
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const blobToDataUrlViaCanvas = async (blob) => {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return canvas.toDataURL("image/jpeg", 0.92);
};

const isImageBlob = async (blob) => {
  if (blob.type?.startsWith("image/")) {
    return true;
  }

  const header = await blob.slice(0, 12).arrayBuffer();
  const bytes = new Uint8Array(header);

  if (bytes[0] === 0xff && bytes[1] === 0xd8) return true;
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e) return true;
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return true;
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46) return true;

  // XML / HTML error pages (e.g. S3 NoSuchKey)
  if (bytes[0] === 0x3c) return false;

  return false;
};

const fetchImageBlob = async (url) => {
  const cacheBustedUrl = `${url}${url.includes("?") ? "&" : "?"}crop=${Date.now()}`;
  const response = await fetch(cacheBustedUrl, {
    mode: "no-cors",
    credentials: "omit",
    cache: "no-store",
  });
  const blob = await response.blob();

  if (!blob?.size) {
    throw new Error("empty blob");
  }

  if (!(await isImageBlob(blob))) {
    throw new Error("invalid image blob");
  }

  return blob;
};

const imageUrlToCropSrc = async (urls) => {
  const urlList = Array.isArray(urls) ? urls : [urls];
  let lastError;

  for (const url of urlList) {
    if (!url) continue;

    if (url.startsWith("data:")) {
      return url;
    }

    try {
      const blob = await fetchImageBlob(url);

      try {
        return await blobToDataUrl(blob);
      } catch {
        return await blobToDataUrlViaCanvas(blob);
      }
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("unable to load image");
};

const dataUrlToBlob = (dataUrl) => {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
  const bytes = atob(data);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) {
    buffer[i] = bytes.charCodeAt(i);
  }
  return new Blob([buffer], { type: mime });
};

const ProductEditImg = () => {
  const { product, stationId, productCategories } = useSelector((state) => ({
    product: state.selectedProduct.product,
    stationId: state.user.stationId,
    productCategories: state.selectedProduct.productCategories,
  }));

  const [imagesList, setImagesList] = useState([]);
  const [demoProducts, setDemoProducts] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editCropSrc, setEditCropSrc] = useState(null);
  const [editSrcPrev, setEditSrcPrev] = useState(null);
  const [editCropped, setEditCropped] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (product?.media?.length) {
      setImagesList(product.media);
    } else {
      setImagesList([]);
    }
  }, [product]);

  useEffect(() => {
    const slug = productCategories?.[0]?.slug;
    if (!slug || !stationId) {
      setDemoProducts([]);
      return;
    }

    getItemsPerCategory(slug, stationId)
      .then((res) => {
        const items = Array.isArray(res.data)
          ? res.data
          : res.data?.products || [];
        setDemoProducts(items.slice(0, DEMO_PRODUCTS_COUNT));
      })
      .catch(() => setDemoProducts([]));
  }, [productCategories, stationId]);

  const validImages = useMemo(
    () => imagesList.filter((img) => img?.path),
    [imagesList],
  );

  useEffect(() => {
    if (!validImages.length) {
      setSelectedIndex(0);
      return;
    }

    setSelectedIndex((prev) => {
      if (prev >= validImages.length) {
        return validImages.length - 1;
      }

      const defaultIdx = validImages.findIndex((img) => img.default);
      if (defaultIdx >= 0 && !validImages[prev]?.default) {
        return defaultIdx;
      }

      return prev >= 0 ? prev : validImages.length - 1;
    });
  }, [validImages]);

  const getImageUrl = (img) => buildImageUrlCandidates(img)[0] || "";

  const isGs1Image = (img) => /gs1/i.test(img?.path || "");

  const deleteImage = (index) => {
    showAlertWithConfirmCancel({
      msgTitle: "האם אתה בטוח שברצונך למחוק את התמונה?",
      confirmText: "מחק",
      cancelText: "ביטול",
      onConfirm: async () => {
        dispatch(setPreload(true));

        const newMedia = [
          ...validImages.slice(0, index),
          ...validImages.slice(index + 1),
        ];

        try {
          await updateProductFields(stationId, product.productId, {
            media: newMedia,
          });
          setImagesList(newMedia);
          showAlert({
            msgTitle: "התמונה נמחקה בהצלחה",
            msgType: "success",
          });
        } catch {
          showAlert({
            msgTitle: "שגיאה במחיקת התמונה",
            msgType: "error",
          });
        } finally {
          dispatch(setPreload(false));
        }
      },
    });
  };

  const uploadImg = (data) => {
    if (!data?.media?.length) return;

    const newImages = data.media
      .filter((img) => img?.path)
      .map((img) => ({
        path: img.path,
        size: img.size || 0,
        default: img.default || false,
      }));

    if (!newImages.length) return;

    setImagesList((prev) => [...prev, ...newImages]);
    dispatch(setPreload(false));
  };

  const setDefaultImage = async (index) => {
    setSelectedIndex(index);

    const newMedia = validImages.map((img, i) => ({
      ...img,
      default: i === index,
    }));

    dispatch(setPreload(true));
    try {
      await updateProductFields(stationId, product.productId, {
        media: newMedia,
      });
      setImagesList(newMedia);
    } finally {
      dispatch(setPreload(false));
    }
  };

  const openEditor = async (index) => {
    setSelectedIndex(index);
    dispatch(setPreload(true));

    try {
      const cropSrc = await imageUrlToCropSrc(
        buildImageUrlCandidates(validImages[index]),
      );
      setEditCropSrc(cropSrc);
      setEditSrcPrev(null);
      setEditCropped(false);
      setEditingIndex(index);
    } catch (error) {
      console.error(error);
      showAlert({
        msgTitle: "שגיאה",
        msgText: "לא ניתן לטעון את התמונה לעריכה",
        msgType: "error",
      });
    } finally {
      dispatch(setPreload(false));
    }
  };

  const closeEditor = () => {
    setEditingIndex(null);
    setEditCropSrc(null);
    setEditSrcPrev(null);
    setEditCropped(false);
  };

  const saveEditedImage = async (event, { isDefault: setAsDefault } = {}) => {
    event?.preventDefault();

    if (editingIndex === null) return;

    if (!editCropped || !editSrcPrev) {
      showAlert({
        msgTitle: "שגיאה",
        msgText: "יש ללחוץ על 'גזור' לפני השמירה.",
        msgType: "error",
      });
      return;
    }

    try {
      await replaceImage(editingIndex, editSrcPrev, setAsDefault);
      closeEditor();
    } catch {
      // replaceImage already shows an alert
    }
  };

  const replaceImage = async (index, croppedSrc, setAsDefault = false) => {
    if (!croppedSrc) {
      showAlert({
        msgTitle: "שגיאה",
        msgText: "לא גזרת את התמונה.",
        msgType: "error",
      });
      return;
    }

    dispatch(setPreload(true));

    try {
      const blob = croppedSrc.startsWith("data:")
        ? dataUrlToBlob(croppedSrc)
        : await fetch(croppedSrc).then((response) => response.blob());

      const fileName = `${product.mkt}_${product.gtin}_${Date.now()}.jpg`;
      const file = new File([blob], fileName, {
        type: blob.type || "image/jpeg",
      });

      const { data } = await getPresignedUrl(file);
      if (!data?.uploadUrl) throw new Error("No uploadUrl");

      await fetch(data.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });

      const dimensions = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = reject;
        img.src = croppedSrc;
      });

      const res = await UpdateProduct({
        productId: product.productId,
        media: {
          path: file.name,
          size: file.size,
          ...dimensions,
          order: index,
          default: setAsDefault,
        },
      });

      const newPath = res.data.addedImages?.[0] || file.name;
      const newMedia = validImages.map((img, i) => {
        if (i === index) {
          return {
            path: newPath,
            size: file.size,
            ...dimensions,
            default: setAsDefault || img.default,
          };
        }
        return setAsDefault ? { ...img, default: false } : img;
      });

      await updateProductFields(stationId, product.productId, {
        media: newMedia,
      });
      setImagesList(newMedia);
    } catch (err) {
      console.error(err);
      showAlert({
        msgTitle: "שגיאה",
        msgText: "העלאת התמונה נכשלה",
        msgType: "error",
      });
      throw err;
    } finally {
      dispatch(setPreload(false));
    }
  };

  if (!product) return null;

  const previewElement =
    demoProducts.find((item) => item.productId === product.productId) ||
    product;

  const selectedImage = validImages[selectedIndex];

  return (
    <div className="product-edit-img">
      {editingIndex !== null && editCropSrc ? (
        <MyCropperComp
          src={editCropSrc}
          srcPrev={editSrcPrev}
          setSrcPrev={setEditSrcPrev}
          cancel={closeEditor}
          save={saveEditedImage}
          cropped={editCropped}
          setCropped={setEditCropped}
          element={previewElement}
        />
      ) : null}

      <section className="product-edit-img__section">
        <div className="product-edit-img__upload-row">
          <span className="product-edit-img__upload-label">הוסף תמונה</span>
          <MyCropper
            uploadImg={uploadImg}
            itemId={product.productId}
            beforeFileName={`${product.mkt}_${product.gtin}_`}
            element={previewElement}
          />
        </div>
      </section>

      {selectedImage ? (
        <section className="product-edit-img__section">
          <h3 className="product-edit-img__section-title">תמונה נוכחית</h3>
          <div className="product-edit-img__hero">
            <img
              className="product-edit-img__hero-image"
              src={getImageUrl(selectedImage)}
              alt="תמונה נוכחית"
            />
            <button
              type="button"
              className="product-edit-img__hero-edit"
              onClick={() => openEditor(selectedIndex)}
            >
              <img src={`${globalFileIcon}edit.svg`} alt="" />
              <span>ערוך וגזור</span>
            </button>
          </div>
        </section>
      ) : null}

      <section className="product-edit-img__section">
        <h3 className="product-edit-img__section-title">כל התמונות במערכת</h3>
        {validImages.length ? (
          <div className="product-edit-img__gallery">
            {validImages.map((img, index) => (
              <EditableImage
                key={`${img.path}-${index}`}
                imageUrl={getImageUrl(img)}
                isSelected={index === selectedIndex}
                isDefault={Boolean(img.default)}
                isGs1={isGs1Image(img)}
                onSelect={() => setDefaultImage(index)}
                onEdit={() => openEditor(index)}
                onDelete={() => deleteImage(index)}
              />
            ))}
          </div>
        ) : (
          <div className="product-edit-img__empty">אין רשימה להצגה</div>
        )}
      </section>

      <section className="product-edit-img__section">
        <h3 className="product-edit-img__section-title">הדגמה במערכת</h3>
        {demoProducts.length > 1 ? (
          <div className="product-edit-img__demo-grid">
            {demoProducts.map((item) => (
              <div
                key={item.productId || item.gtin}
                className="product-edit-img__demo-card item-card role"
              >
                <Detailes
                  inCart={false}
                  productPopUp={() => {}}
                  element={item}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="product-edit-img__empty">אין רשימה להצגה</div>
        )}
      </section>
    </div>
  );
};

const EditableImage = ({
  imageUrl,
  isSelected,
  isDefault,
  isGs1,
  onSelect,
  onEdit,
  onDelete,
}) => {
  const handleEdit = (event) => {
    event.stopPropagation();
    onEdit();
  };

  return (
    <div className="product-edit-img__thumb-wrap">
      <div
        className={`product-edit-img__thumb${
          isSelected || isDefault ? " product-edit-img__thumb--active" : ""
        }`}
      >
        <img
          className="product-edit-img__thumb-image"
          src={imageUrl}
          alt=""
          onClick={onSelect}
          title="הגדר כתמונה ראשית"
        />
        <button
          type="button"
          className="product-edit-img__icon-btn product-edit-img__icon-btn--edit"
          title="עריכת תמונה"
          aria-label="עריכת תמונה"
          onClick={handleEdit}
        >
          <img src={`${globalFileIcon}edit.svg`} alt="" />
        </button>
        <button
          type="button"
          className="product-edit-img__icon-btn product-edit-img__icon-btn--delete"
          title="מחיקת תמונה"
          aria-label="מחיקת תמונה"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <img src={`${globalFileIcon}deleteIcon.svg`} alt="" />
        </button>
      </div>

      <label className="product-edit-img__gs1-label">
        {isGs1 ? "תמונה של GS1" : ""}
      </label>
    </div>
  );
};

export default ProductEditImg;
